import {PROMOTION,campaignAt,groupedCatalog,discountCodes} from './pressroom-campaigns.js';

export async function loadCatalog(root, request=fetch) {
  const products=[];
  for(let page=1;page<=250;page++) {
    // This is a Liquid template that emits JSON, not Shopify's JSON route.
    // Asking for application/json makes Shopify return 404 before Liquid runs.
    const response=await request(`${root}collections/all?view=pressroom-feed&page=${page}`,{headers:{Accept:'text/html'},signal:AbortSignal.timeout(15000)});
    if(!response.ok)throw Error(`Catalog request failed (${response.status})`);
    const data=await response.json();
    if(!Array.isArray(data.products)||!Number.isInteger(data.pages)||data.pages<0||data.pages>250)throw Error('Invalid catalog feed');
    products.push(...data.products);
    if(page>=data.pages)return products;
  }
  throw Error('Catalog exceeds Shopify pagination limit');
}

export async function applyCodes(root, added, request=fetch) {
  const options={headers:{'Content-Type':'application/json'},signal:AbortSignal.timeout(10000)};
  const response=await request(`${root}cart.js`,options);
  if(!response.ok)throw Error(`Unable to read cart (${response.status})`);
  const cart=await response.json(), codes=discountCodes(cart,added);
  if(JSON.stringify(codes)===JSON.stringify(discountCodes(cart,[])))return cart;
  const result=await request(`${root}cart/update.js`,{...options,method:'POST',body:JSON.stringify({discount:codes.join(',')})});
  if(!result.ok)throw Error(`Unable to apply offer (${result.status})`);
  return result.json();
}

export async function startStorefront(doc=document, win=window, request=fetch, date=()=>new Date()) {
  const element=doc.getElementById('pressroom-storefront-config');
  if(!element)return;
  const config=JSON.parse(element.textContent);
  const root=config.root.endsWith('/')?config.root:config.root+'/';
  const money=cents=>new Intl.NumberFormat(doc.documentElement.lang||'en-US',{style:'currency',currency:config.currency||'USD'}).format(cents/100);
  const activeCampaign=()=>config.discountsReady&&config.country==='US'&&config.currency==='USD'?campaignAt(date()):null;
  const campaign=activeCampaign();
  const banner=doc.getElementById('pressroom-campaign');
  if(campaign&&banner) {
    doc.body.prepend(banner);
    const end=campaign[1].split('-').map(Number);
    const endLabel=new Intl.DateTimeFormat('en-US',{month:'long',day:'numeric',timeZone:'UTC'}).format(new Date(Date.UTC(2024,end[0]-1,end[1])));
    banner.querySelector('[data-pr-campaign-text]').textContent=`40% off all shirts through ${endLabel}`;
    banner.hidden=false;
  }

  const paintPrices=()=>{
    for(const node of doc.querySelectorAll('[data-pr-regular-cents]')) {
      if(node.dataset.prPainted)continue;
      node.dataset.prPainted='true';
      if(!campaign||Number(node.dataset.prRegularCents)!==PROMOTION.regularCents)continue;
      const price=doc.createElement('span');price.className='pr-offer-price';
      const regular=doc.createElement('s');regular.textContent=money(PROMOTION.regularCents);
      const sale=doc.createElement('span');sale.textContent=money(PROMOTION.regularCents-PROMOTION.savingsCents);
      price.append(regular,sale);
      node.hidden=true;node.after(price);
    }
  };
  paintPrices();
  const refreshCampaign=()=>{
    if(JSON.stringify(campaign)!==JSON.stringify(activeCampaign()))win.location.reload();
  };
  win.setInterval(refreshCampaign,60000);
  doc.addEventListener('visibilitychange',()=>{if(doc.visibilityState==='visible')refreshCampaign();});
  let painting=false;
  const observer=new win.MutationObserver(()=>{
    if(painting)return;
    painting=true;queueMicrotask(()=>{paintPrices();painting=false;});
  });
  observer.observe(doc.body,{childList:true,subtree:true});

  // Intercept the normal cart checkout only. Native accelerated checkouts are
  // left intact; the visible coupon works there too. Never remove an old code
  // or touch a cart on a full-price day merely because the calendar advanced.
  const showCartNote=(target,message)=>{
    let note=doc.getElementById('pr-cart-note');
    if(!note){note=doc.createElement('p');note.id='pr-cart-note';note.className='pr-cart-note';note.setAttribute('role','status');target.after(note);}
    note.textContent=message;
  };
  let checkingOut=false;
  const checkout=async event=>{
    const target=event.target.closest('button[name="checkout"],input[name="checkout"],a[href="/checkout"]');
    const offer=activeCampaign();
    if(!target||!offer)return;
    event.preventDefault();event.stopImmediatePropagation();
    if(checkingOut)return;checkingOut=true;
    target.setAttribute('aria-busy','true');
    try {
      const cart=await applyCodes(root,[offer[3]],request);
      if(!cart.items?.length){showCartNote(target,'Your cart is empty. Add a shirt to use your allocation.');return;}
      win.location.assign(root+'checkout');
    } catch(error) {
      showCartNote(target,`We could not apply the offer automatically. Use ${offer[3]} at checkout. ${error.message}`);
      const link=doc.createElement('a');link.href=root+'checkout';link.textContent='Continue to checkout';target.after(link);
    } finally {checkingOut=false;target.removeAttribute('aria-busy');}
  };
  doc.addEventListener('click',checkout,true);
  doc.addEventListener('submit',event=>{
    if(event.submitter?.matches('[name="checkout"]'))checkout({target:event.submitter,preventDefault:()=>event.preventDefault(),stopImmediatePropagation:()=>event.stopImmediatePropagation()});
  },true);

  const addSale=()=>{
    const offer=activeCampaign();
    if(offer)
      applyCodes(root,[offer[3]],request).catch(error=>console.error('Pressroom offer: use the displayed code at checkout',error));
  };
  if(campaign)addSale();
  if(typeof win.subscribe==='function'&&win.PUB_SUB_EVENTS?.cartUpdate)win.subscribe(win.PUB_SUB_EVENTS.cartUpdate,addSale);

  doc.querySelector('[data-pr-welcome]')?.addEventListener('click',async event=>{
    const button=event.currentTarget;button.disabled=true;
    try {
      const add=[PROMOTION.welcomeCode];
      const offer=activeCampaign();
      if(offer)add.unshift(offer[3]);
      await applyCodes(root,add,request);
      button.textContent='Benefits saved. Shop the catalog';
      button.onclick=()=>win.location.assign(root+'collections/all');
    }catch(error){showCartNote(button,`Use ${PROMOTION.welcomeCode} at checkout with your signup email. ${error.message}`);}
    finally{button.disabled=false;}
  },{once:true});

  const params=new URLSearchParams(win.location.search), featured=params.get('featured');
  const filtered=[...params.keys()].some(key=>key.startsWith('filter.')||key==='sort_by');
  if(config.collection!=='all'||filtered)return;
  const native=doc.querySelector('[id^="shopify-section-"]:has(#product-grid)');
  if(!native)return;
  try {
    const products=await loadCatalog(root,request);
    if(!products.length)return;
    const container=doc.createElement('section');container.className='pr-catalog page-width';container.setAttribute('aria-label','Catalog');
    const browse=doc.createElement('a');browse.href=root+'collections/all?sort_by=title-ascending';browse.textContent='Browse with filters and sorting';container.append(browse);
    let groups=groupedCatalog(products,featured);
    const niche=params.get('niche');if(niche)groups=[...groups.filter(([name])=>name===niche),...groups.filter(([name])=>name!==niche)];
    for(const [name,items] of groups) {
      const section=doc.createElement('section');section.className='pr-niche';
      const heading=doc.createElement('h2');heading.textContent=name.replaceAll('-',' ');section.append(heading);
      const grid=doc.createElement('ul');grid.className='pr-products';section.append(grid);
      for(const product of items) {
        const card=doc.createElement('li');card.className='pr-product';
        if(product.handle===featured)card.classList.add('pr-featured');
        const link=doc.createElement('a');
        const url=new URL(product.url,win.location.origin);if(url.origin!==win.location.origin)throw Error('Invalid product URL');
        link.href=url.pathname+url.search;
        if(product.image){
          const image=doc.createElement('img');image.src=product.image;image.alt=product.title;image.width=720;image.height=720;image.loading='lazy';link.append(image);
        }
        if(product.handle===featured){const label=doc.createElement('span');label.className='pr-featured-label';label.textContent='The shirt you came for';link.append(label);}
        const title=doc.createElement('h3');title.textContent=product.title;link.append(title);
        const price=doc.createElement('span');price.textContent=money(product.price);
        if(product.tags.includes('pressroom'))price.dataset.prRegularCents=product.price;
        link.append(price);card.append(link);grid.append(card);
      }
      container.append(section);
    }
    // Load every feed page before replacing the native grid. Reordering only
    // page one loses advertised products and silently drops other niches.
    native.before(container);native.hidden=true;paintPrices();
  }catch(error){console.error('Pressroom catalog: native grid retained',error);}
  return {observer};
}
if(typeof document!=='undefined')startStorefront().catch(error=>console.error('Pressroom storefront',error));
